import _ from 'underscore';
import { formatMetricValue } from '@bingads-webui-campaign/campaignui-common-columns';

import headerTemplate from './header.pug';
import cellTemplate from './cell.pug';

const biPattern = /^(Phone)?PerformanceMetrics\//;
const phoneBiPattern = /^PhonePerformanceMetrics\//;
const biReplacePattern = /PerformanceMetrics\//;
const biSubColPrefix = 'PopSubCol';

function getPoPCols(column, popTitles, i18n) {
  const prop = column.property;
  let propertyKey = _.isString(prop) ? prop : prop.key;
  let parentProperty = null;
  let isColumnWithFluc = false;

  // Handle performance metric column with fluctuations
  if (prop && prop.key && _.isArray(prop.key)) {
    propertyKey = _.find(prop.key, key => biPattern.test(key));
    parentProperty = prop;
    isColumnWithFluc = true;
  }

  const columnWithFlucTemplate = ({ value }) => {
    // eslint-disable-next-line max-len
    // Hack here: Columns with fluctuation are used in [Clics, Impressions, Spend]. Only Spend's format is decimal. Spend column in performance grid is called TotalEffectiveCost.
    const format = column.name === 'Spend' || column.name === 'TotalEffectiveCost' ? 'decimal' : 'integer';
    return cellTemplate({ value: formatMetricValue({ i18n, format, metricValue: value }) });
  };

  const { subColumns = {}, template: parentTemplate, ...parentColumn } = column;

  const primaryName = `${biSubColPrefix}${column.name}`;
  const primary = _.defaults({
    name: primaryName,
    property: parentProperty || propertyKey,
    helpId: null,
    title: popTitles.primary,
    width: column[`${biSubColPrefix}Width`],
    headAttributes: _.defaults(
      { 'aria-label': popTitles.primary },
      { 'data-column': primaryName },
      { style: `width: ${column[`${biSubColPrefix}Width`]}px` },
      column.headAttributes || {}
    ),
    bodyAttributes: {
      'data-column': primaryName,
      style: `width: ${column[`${biSubColPrefix}Width`]}px`,
    },
    template: parentTemplate,
  }, subColumns[primaryName], parentColumn);

  const compareName = `Compared${column.name}`;
  const compare = _.defaults({
    name: compareName,
    property: phoneBiPattern.test(propertyKey) ?
      propertyKey.replace(phoneBiPattern, 'PhonePeriodPerformanceMetrics/') :
      `Period${propertyKey}`,
    helpId: null,
    width: column.ComparedWidth,
    title: popTitles.compared,
    sortable: _.defaults({
      key: `PoP${propertyKey}`,
    }, column.sortable),
    headAttributes: _.defaults(
      { 'aria-label': popTitles.compared },
      { 'data-column': compareName },
      { style: `width: ${column.ComparedWidth}px` },
      column.headAttributes || {}
    ),
    bodyAttributes: {
      'data-column': compareName,
      style: `width: ${column.ComparedWidth}px`,
    },
    template: isColumnWithFluc ? columnWithFlucTemplate : parentTemplate,
  }, subColumns[compareName], parentColumn);

  let property = propertyKey.replace(biReplacePattern, 'PerformanceMetricsChange/');
  const changeTitle = i18n.getString('PoP_Sub_Title_Change');
  const changeName = `${column.name}Change`;
  const change = _.defaults({
    name: changeName,
    property,
    helpId: 'pop_BA_Reporting_Clarity_POPChange',
    title: changeTitle,
    width: column.ChangeWidth,
    helpAriaLabel: i18n.getString(_TL_('Click to learn more about {{item}}'), { item: changeTitle }),
    sortable: _.defaults({
      key: `PoP${property}`,
    }, column.sortable),
    headAttributes: _.defaults(
      { 'aria-label': changeTitle },
      { 'data-column': changeName },
      { style: `width: ${column.ChangeWidth}px` },
      column.headAttributes || {}
    ),
    bodyAttributes: {
      'data-column': changeName,
      style: `width: ${column.ChangeWidth}px`,
    },
    template: isColumnWithFluc ? columnWithFlucTemplate : parentTemplate,
  }, subColumns[changeName], parentColumn);

  property = propertyKey.replace(biReplacePattern, 'PerformanceMetricsChangePercentage/');
  const changePercentTitle = i18n.getString('PoP_Sub_Title_Change_Percentage');
  const changePercentName = `${column.name}ChangePercentage`;
  const changePercent = _.defaults({
    name: changePercentName,
    property,
    helpId: 'pop_BA_Reporting_Clarity_POPChangePercent',
    title: changePercentTitle,
    width: column.PopSubColWidth,
    helpAriaLabel: i18n.getString(_TL_('Click to learn more about {{item}}'), { item: changePercentTitle }),
    sortable: _.defaults({
      key: `PoP${property}`,
    }, column.sortable),
    headAttributes: _.defaults(
      { 'aria-label': changePercentTitle },
      { 'data-column': changePercentName },
      { style: `width: ${column.ChangePercentageWidth}px` },
      column.headAttributes || {}
    ),
    bodyAttributes: {
      'data-column': changePercentName,
      style: `width: ${column.ChangePercentageWidth}px`,
    },
    template: ({ value }) => {
      let formatted;
      // Show ∞ instead of NaN.00% if MT returns double.MaxValue or float.MaxValue
      if (value > 3.4e+38) {
        formatted = '∞';
      } else {
        formatted = i18n.formatPercent(value / 100, {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        });
      }
      return cellTemplate({ value: formatted });
    },
  }, subColumns[changePercentName], parentColumn);

  const columns = [primary, compare, change, changePercent];

  return _.map(columns, col => _.defaults({
    html: headerTemplate(col),
  }, col));
}

function getPopTitles(dateRange) {
  const primary = dateRange.formatedStart === dateRange.formatedEnd
    && dateRange.RangeStartDate === dateRange.RangeEndDate ?
    dateRange.startDate || dateRange.RangeStartDate :
    `${dateRange.formatedStart || dateRange.RangeStartDate} - ${dateRange.formatedEnd || dateRange.RangeEndDate}`;
  const compared = dateRange.compared.formattedStart === dateRange.compared.formattedEnd ?
    dateRange.compared.formattedStart :
    `${dateRange.compared.formattedStart} - ${dateRange.compared.formattedEnd}`;

  return { primary, compared };
}

function updateSubCols(columnsCur, expandedCols, popTitles, i18n) {
  return _.map(columnsCur, (column) => {
    if (_.contains(expandedCols, column.name)) {
      return _.defaults({ columns: getPoPCols(column, popTitles, i18n) }, column);
    } else if (biPattern.test(column.property)) {
      return _.omit(column, 'columns');
    } else if (column.columns && column.columns.length > 0) {
      return _.defaults({ columns: updateSubCols(column.columns, expandedCols) }, column);
    }

    return column;
  });
}

export default {
  name: 'popColumns',
  handler: function popColumns(state, { expandedCols, dateRange, i18n } = {}) {
    const isComparing = Boolean(dateRange.compared);
    const expanded = isComparing ? expandedCols : [];
    const popTitles = isComparing ? getPopTitles(dateRange) : null;

    const columns = updateSubCols(state.columns, expanded, popTitles, i18n);

    return _.defaults({
      columns,
      popStatus: { isComparing },
    }, state);
  },
  defaults: {
    expandedCols: [],
    dateRange: {},
  },
};
